/**
 * The company Company Settings shows, kept on this device so its header, the
 * modal's rail head and a SaleFish Admin's company switcher draw at once on a
 * cold load instead of waiting on `/api/company-admin/company` (Andrew,
 * 2026-10-31: "Fix all the loading skeletons"). Features & billing was the
 * last screen whose skeleton differed from the page in its header and rail,
 * because the company — its name, logo, type and address — was not known
 * until it loaded.
 *
 * Built exactly like lib/projectsSnapshot.ts and under the same three rules,
 * which tests/companySnapshot.test.ts pins:
 *
 * DISPLAY ONLY — never the query cache. Drawn while the live company is still
 * loading and replaced by it the moment it lands. It is NOT seeded into React
 * Query (`initialData` / `placeholderData` / `setQueryData`): the same key
 * feeds the Company details form, and a form initialised from a stale copy
 * would save stale values. Here the worst a stale copy can do is show a
 * renamed company's old name for the length of one request.
 *
 * ONE ACCOUNT. The copy names the user it belongs to and is read only for
 * that user — the account AuthContext publishes with
 * `setCompanySnapshotOwner` — so a shared machine whose last session merely
 * expired never shows the next person someone else's company. Sign-out
 * removes it.
 *
 * Only what those surfaces draw: per company scope (lib/companyScope.ts
 * `scopeKey`), the name, the type, the address, whether it is a SaleFish
 * Partner (the chip under its name) and the logo's PATH; and per project, the
 * switcher's choices (name, logo path, SaleFish or not, partner or not). No
 * phone, no members, no projects, no billing, nothing a form reads.
 */

const STORE = "salefish:company:v1";
/** A SaleFish Admin may look at a handful of companies; keep the last few. */
const MAX_COMPANIES = 12;
const MAX_SCOPE_LISTS = 12;
const MAX_OPTIONS = 60;

export type SnapshotCompanyProfile = {
  name: string;
  type: string | null;
  address: string | null;
  logoUrl: string | null;
  partner: boolean;
};

export type SnapshotScopeOption = {
  id: string;
  companyName: string;
  logoUrl: string | null;
  isSaleFish: boolean;
  partner: boolean;
};

type Stored = {
  userId: string;
  /** By company scope key, most recent last. */
  companies: Array<[string, SnapshotCompanyProfile]>;
  /** By the project the switcher was opened from ("" for none), most recent last. */
  scopes: Array<[string, SnapshotScopeOption[]]>;
};

/** Who AuthContext says is signed in, this page load. Null until it knows. */
let owner: string | null = null;

export function setCompanySnapshotOwner(userId: string | null | undefined): void {
  owner = userId ?? null;
}

/** A same-origin path, never `data:`/`blob:`/protocol-relative. */
const path = (s: unknown): string | null =>
  typeof s === "string" && s.startsWith("/") && !s.startsWith("//") ? s : null;
const text = (s: unknown): string | null => (typeof s === "string" && s ? s : null);

function readStored(): Stored | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORE) ?? "null");
    if (!parsed || typeof parsed.userId !== "string") return null;
    return {
      userId: parsed.userId,
      companies: Array.isArray(parsed.companies) ? parsed.companies : [],
      scopes: Array.isArray(parsed.scopes) ? parsed.scopes : [],
    };
  } catch {
    return null;
  }
}

function write(stored: Stored): void {
  try {
    const next = JSON.stringify(stored);
    if (localStorage.getItem(STORE) !== next) localStorage.setItem(STORE, next);
  } catch {
    /* private mode or full storage — the next load waits for the network, as before */
  }
}

/** This account's copy to add to, or a fresh one when the stored copy is someone else's. */
function mine(userId: string): Stored {
  const prev = readStored();
  return prev && prev.userId === userId ? prev : { userId, companies: [], scopes: [] };
}

function put<T>(list: Array<[string, T]>, key: string, value: T, max: number): Array<[string, T]> {
  return [...list.filter(([k]) => k !== key), [key, value] as [string, T]].slice(-max);
}

/**
 * Keep the company the page just loaded, for the signed-in account and this
 * scope. `partner` is the features answer's `complimentary` when it is known;
 * left out, the stored value is kept.
 */
export function saveCompanySnapshot(scopeKey: string, company: Record<string, any> | null | undefined, partner?: boolean): void {
  if (!owner || !company || typeof company !== "object") return;
  const stored = mine(owner);
  const prev = stored.companies.find(([k]) => k === scopeKey)?.[1];
  const profile: SnapshotCompanyProfile = {
    name: text(company.companyName) ?? text(company.name) ?? "",
    type: text(company.type),
    address: text(company.address),
    logoUrl: path(company.logoUrl),
    partner: partner ?? prev?.partner ?? false,
  };
  write({ ...stored, companies: put(stored.companies, scopeKey, profile, MAX_COMPANIES) });
}

/** Keep the companies a SaleFish Admin's switcher offered, opened from `projectId`. */
export function saveCompanyScopesSnapshot(projectId: string | null | undefined, options: ReadonlyArray<Record<string, any>> | null | undefined): void {
  if (!owner || !Array.isArray(options)) return;
  const stored = mine(owner);
  const rows: SnapshotScopeOption[] = options
    .filter((o) => o && typeof o.id === "string")
    .slice(0, MAX_OPTIONS)
    .map((o) => ({
      id: o.id,
      companyName: typeof o.companyName === "string" ? o.companyName : "",
      logoUrl: path(o.logoUrl),
      isSaleFish: !!o.isSaleFish,
      partner: !!o.partner,
    }));
  write({ ...stored, scopes: put(stored.scopes, projectId ?? "", rows, MAX_SCOPE_LISTS) });
}

/** The signed-in reader's copy of this scope's company, or null (nobody known, someone else's, never seen). */
export function readCompanySnapshot(scopeKey: string): SnapshotCompanyProfile | null {
  if (!owner) return null;
  const stored = readStored();
  if (!stored || stored.userId !== owner) return null;
  return stored.companies.find(([k]) => k === scopeKey)?.[1] ?? null;
}

/** The switcher's choices as this reader last saw them from `projectId`, or null. */
export function readCompanyScopesSnapshot(projectId: string | null | undefined): SnapshotScopeOption[] | null {
  if (!owner) return null;
  const stored = readStored();
  if (!stored || stored.userId !== owner) return null;
  return stored.scopes.find(([k]) => k === (projectId ?? ""))?.[1] ?? null;
}

export function forgetCompanySnapshot(): void {
  owner = null;
  try {
    localStorage.removeItem(STORE);
  } catch {
    /* nothing to forget */
  }
}
